/**
 * Global audio player state management
 */
import { create } from 'zustand';
import type { Session, LocalizedSession, SessionDisplay } from '@/types';

// Union type for any session type that can be played
type PlayableSession = Session | LocalizedSession | SessionDisplay;

interface AudioPlayerState {
  currentSession: PlayableSession | null;
  isPlaying: boolean;
  isVisible: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  /** Position the next load starts at ("Nastavi slušanje"); FixedAudioPlayer applies and clears it. */
  startAt: number | null;
  setCurrentSession: (session: PlayableSession | null) => void;
  setIsPlaying: (isPlaying: boolean) => void;
  setIsVisible: (isVisible: boolean) => void;
  setCurrentTime: (time: number) => void;
  setDuration: (duration: number) => void;
  setVolume: (volume: number) => void;
  setIsMuted: (isMuted: boolean) => void;
  playSession: (session: PlayableSession) => void;
  /** Play `session` from `position` seconds (Q23 resume). */
  resumeSession: (session: PlayableSession, position: number) => void;
  clearStartAt: () => void;
  /** Replace the current session's audio URL (fetched, or refreshed after expiry). */
  setAudioUrl: (sessionId: string, url: string) => void;
  closePlayer: () => void;
}

export const useAudioPlayerStore = create<AudioPlayerState>((set) => ({
  currentSession: null,
  isPlaying: false,
  isVisible: false,
  currentTime: 0,
  duration: 0,
  volume: 1,
  isMuted: false,
  startAt: null,
  setCurrentSession: (session) => set({ currentSession: session }),
  setIsPlaying: (isPlaying) => set({ isPlaying }),
  setIsVisible: (isVisible) => set({ isVisible }),
  setCurrentTime: (time) => set({ currentTime: time }),
  setDuration: (duration) => set({ duration }),
  setVolume: (volume) => set({ volume }),
  setIsMuted: (isMuted) => set({ isMuted }),
  playSession: (session) => set((state) => ({
    // Same session again: keep the loaded URL. A new signed URL would reload
    // the <audio> element and lose the position.
    currentSession:
      state.currentSession?.id === session.id && state.currentSession.audio_url
        ? { ...session, audio_url: state.currentSession.audio_url }
        : session,
    isVisible: true,
    isPlaying: true,
    // Only reset time if switching to a different session
    currentTime: state.currentSession?.id === session.id ? state.currentTime : 0,
    startAt: null,
  })),
  resumeSession: (session, position) => set({
    currentSession: session,
    isVisible: true,
    isPlaying: true,
    currentTime: position,
    startAt: position,
  }),
  clearStartAt: () => set({ startAt: null }),
  setAudioUrl: (sessionId, url) => set((state) =>
    state.currentSession?.id === sessionId
      ? { currentSession: { ...state.currentSession, audio_url: url } }
      : {}
  ),
  closePlayer: () => set({
    isVisible: false,
    isPlaying: false,
    currentSession: null,
    currentTime: 0,
    startAt: null,
  }),
}));

// Selector hooks for convenience
export const useCurrentSession = () => useAudioPlayerStore((state) => state.currentSession);
export const useIsPlayerVisible = () => useAudioPlayerStore((state) => state.isVisible);
export const usePlaySession = () => useAudioPlayerStore((state) => state.playSession);
export const useResumeSession = () => useAudioPlayerStore((state) => state.resumeSession);
export const useClosePlayer = () => useAudioPlayerStore((state) => state.closePlayer);
